import { Step } from 'react-joyride';
import { IntlShape } from 'react-intl';

// --- Tab key → i18n content description mapping ---
// Only the *content* (description) needs i18n keys.
// The *title* is read directly from the rendered tab element's text,
// so it always matches the tab label in the current language.

const tabContentKey: Record<string, string> = {
  list: 'tourListContent',
  tree: 'tourTreeContent',
  live: 'tourLiveContent',
  overview: 'tourOverviewContent',
  batteryview: 'tourBatteryviewContent',
  pvview: 'tourPvviewContent',
  log: 'tourLogContent',
  information: 'tourInformationContent',
  report: 'tourReportContent',
  manage: 'tourManageContent',
  configuration: 'tourConfigurationContent',
  history: 'tourHistoryContent',
  installationTickets: 'tourInstallationTicketsContent'
};

/**
 * Build tour steps dynamically from the DOM.
 * Scans for all rendered `#tour-tab-*` elements and creates a step for each.
 * The step title is the tab's own rendered text (matching across languages).
 */
export function buildDynamicTourSteps(intl: IntlShape, isInsideInstallation: boolean): Step[] {
  const steps: Step[] = [];

  // Language selector step (only on list/tree pages, not inside an installation)
  if (!isInsideInstallation) {
    const langEl = document.querySelector('[data-tour="language-selector"]');
    if (langEl) {
      steps.push({
        target: '[data-tour="language-selector"]',
        title: intl.formatMessage({ id: 'tourLanguageTitle' }),
        content: intl.formatMessage({ id: 'tourLanguageContent' }),
        placement: 'bottom',
        disableBeacon: true
      });
    }
  }

  // Collect all tour-tab elements in DOM order
  const tabEls = document.querySelectorAll<HTMLElement>('[id^="tour-tab-"]');
  tabEls.forEach((el) => {
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const tabValue = el.id.replace('tour-tab-', '');

    // Skip list/tree tabs when inside an installation (already covered on list page)
    if (isInsideInstallation && (tabValue === 'list' || tabValue === 'tree')) return;

    // Use the tab's own rendered text as title (matches current language)
    const title = el.textContent?.trim() || tabValue;
    const contentKey = tabContentKey[tabValue];
    const content = contentKey
      ? intl.formatMessage({ id: contentKey })
      : '';

    steps.push({
      target: `#tour-tab-${tabValue}`,
      title,
      content,
      placement: 'bottom' as const,
      ...(steps.length === 0 ? { disableBeacon: true } : {})
    });
  });

  // "Explore" hint at the end when on list/tree page
  if (!isInsideInstallation && document.querySelector('#tour-tab-list')) {
    steps.push({
      target: '#tour-tab-list',
      title: intl.formatMessage({ id: 'tourExploreTitle' }),
      content: intl.formatMessage({ id: 'tourExploreContent' }),
      placement: 'bottom'
    });
  }

  return steps;
}
